/**
 * exportToEmailHtml.js
 *
 * Converts BlockNote editor content to email-safe HTML with inline styles.
 * Uses blocksToHTMLLossy as the base and post-processes the output to add
 * inline styles that email clients require (tables, headings, lists, etc.).
 */

// ─── Inline style constants ────────────────────────────────────────────────────

const FONT_FAMILY =
  "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

const STYLES = {
  body: `font-family: ${FONT_FAMILY}; font-size: 14px; line-height: 1.5; color: #333; margin: 0; padding: 0; background-color: #f9f9f9;`,
  container: `max-width: 600px; margin: 0 auto; padding: 24px 20px; background-color: #ffffff;`,
  h1: `font-size: 28px; font-weight: 700; line-height: 1.3; margin: 0 0 12px 0; color: #1a1a1a;`,
  h2: `font-size: 22px; font-weight: 700; line-height: 1.3; margin: 20px 0 10px 0; color: #1a1a1a;`,
  h3: `font-size: 18px; font-weight: 700; line-height: 1.3; margin: 16px 0 8px 0; color: #1a1a1a;`,
  p: `font-size: 14px; line-height: 1.6; margin: 0 0 10px 0; color: #333;`,
  table: `border-collapse: collapse; width: 100%; margin: 12px 0; border: 1px solid #ddd;`,
  th: `border: 1px solid #ddd; padding: 8px 12px; text-align: left; font-weight: 600; background-color: #f5f5f5; font-size: 13px;`,
  td: `border: 1px solid #ddd; padding: 8px 12px; text-align: left; font-size: 13px; vertical-align: top;`,
  ol: `margin: 4px 0 12px 0; padding-left: 24px; font-size: 14px; line-height: 1.6;`,
  ul: `margin: 4px 0 12px 0; padding-left: 24px; font-size: 14px; line-height: 1.6;`,
  li: `margin: 2px 0; font-size: 14px; line-height: 1.6;`,
  hr: `border: none; border-top: 1px solid #e0e0e0; margin: 16px 0;`,
  a: `color: #1a73e8; text-decoration: underline;`,
  img: `max-width: 100%; height: auto; display: block; margin: 8px 0;`,
  blockquote: `border-left: 3px solid #ddd; margin: 12px 0; padding: 8px 16px; color: #666; font-style: italic;`,
  code: `background-color: #f4f4f4; padding: 2px 6px; border-radius: 3px; font-family: 'SF Mono', Monaco, Consolas, monospace; font-size: 13px;`,
  pre: `background-color: #f4f4f4; padding: 12px 16px; border-radius: 6px; overflow-x: auto; margin: 12px 0; font-family: 'SF Mono', Monaco, Consolas, monospace; font-size: 13px; line-height: 1.5;`,
};

// ─── Checklist transform ────────────────────────────────────────────────────────
// BlockNote exports checklists as <ul> with <li data-checked="true|false">
// containing <input type="checkbox">. Email clients strip <input>, so we
// replace them with Unicode checkbox characters.

const CHECKED_ICON = "&#9745;"; // ☑
const UNCHECKED_ICON = "&#9744;"; // ☐

/**
 * Convert a raw HTML string from blocksToHTMLLossy into email-safe HTML
 * with inline styles on every element.
 */
const inlineStyleHtml = (html) => {
  // Use DOMParser to manipulate the HTML properly
  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<div id="__root">${html}</div>`,
    "text/html",
  );
  const root = doc.getElementById("__root");

  // ── Process checklist items first (before general styling) ──
  root.querySelectorAll("li[data-checked]").forEach((li) => {
    const isChecked = li.getAttribute("data-checked") === "true";
    // Remove the <input> checkbox — email clients won't render it
    const input = li.querySelector('input[type="checkbox"]');
    if (input) input.remove();

    // Prepend a Unicode checkbox character
    const icon = isChecked ? CHECKED_ICON : UNCHECKED_ICON;
    const span = doc.createElement("span");
    span.setAttribute(
      "style",
      "margin-right: 6px; font-size: 16px; vertical-align: middle;",
    );
    span.innerHTML = icon;
    li.prepend(span);

    // Style the li itself
    li.setAttribute(
      "style",
      `list-style: none; margin: 4px 0; font-size: 14px; line-height: 1.6;${
        isChecked ? " color: #888; text-decoration: line-through;" : ""
      }`,
    );
    li.removeAttribute("data-checked");
  });

  // ── Apply inline styles to standard elements ──
  const styleMap = {
    h1: STYLES.h1,
    h2: STYLES.h2,
    h3: STYLES.h3,
    h4: STYLES.h3, // reuse h3 for h4+
    h5: STYLES.h3,
    h6: STYLES.h3,
    p: STYLES.p,
    table: STYLES.table,
    th: STYLES.th,
    td: STYLES.td,
    ol: STYLES.ol,
    ul: STYLES.ul,
    li: STYLES.li,
    hr: STYLES.hr,
    a: STYLES.a,
    img: STYLES.img,
    blockquote: STYLES.blockquote,
    code: STYLES.code,
    pre: STYLES.pre,
  };

  Object.entries(styleMap).forEach(([tag, style]) => {
    root.querySelectorAll(tag).forEach((el) => {
      // Don't overwrite styles we already set (e.g. checklist li)
      const existing = el.getAttribute("style") || "";
      if (!existing) {
        el.setAttribute("style", style);
      }
    });
  });

  // ── Strip data-* attributes that email clients don't need ──
  root.querySelectorAll("*").forEach((el) => {
    [...el.attributes].forEach((attr) => {
      if (attr.name.startsWith("data-") || attr.name === "class") {
        el.removeAttribute(attr.name);
      }
    });
  });

  // ── Remove <colgroup> tags (not needed with inline styles) ──
  root.querySelectorAll("colgroup").forEach((el) => el.remove());

  return root.innerHTML;
};

/**
 * Convert a BlockNote editor's document to email-ready HTML string.
 *
 * @param {object} editor - the BlockNote editor instance
 * @returns {Promise<string>} email-compatible HTML string
 */
export const exportToEmailHtml = async (editor) => {
  const rawHtml = await editor.blocksToHTMLLossy(editor.document);
  const styledBody = inlineStyleHtml(rawHtml);

  return `<!DOCTYPE html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
  <!--[if mso]>
  <noscript>
    <xml>
      <o:OfficeDocumentSettings>
        <o:PixelsPerInch>96</o:PixelsPerInch>
      </o:OfficeDocumentSettings>
    </xml>
  </noscript>
  <![endif]-->
  <title>Underwriting Notes</title>
  <style type="text/css">
    /* Reset for email clients */
    body, table, td, p, a, li { -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%; }
    table, td { mso-table-lspace: 0pt; mso-table-rspace: 0pt; }
    img { -ms-interpolation-mode: bicubic; border: 0; outline: none; text-decoration: none; }
  </style>
</head>
<body style="${STYLES.body}">
  <!--[if mso]>
  <table role="presentation" width="600" align="center" cellpadding="0" cellspacing="0" border="0"><tr><td>
  <![endif]-->
  <div style="${STYLES.container}">
    ${styledBody}
  </div>
  <!--[if mso]>
  </td></tr></table>
  <![endif]-->
</body>
</html>`;
};
